import type { Skill } from '../types/skill';
import { scenarioService, type MyScenario } from './scenario';
import { authService } from './auth';
import {
  matchScenarioSkills,
  normalizeSkillKey,
  runWithConcurrency,
  type ScenarioSkillEntry,
  type ScenarioSkillResolution,
  type ScenarioSkillState,
} from './scenarioSkillMatch';

export type { ScenarioSkillEntry, ScenarioSkillResolution, ScenarioSkillState };
export {
  matchScenarioSkills,
  normalizeSkillKey,
  runWithConcurrency,
  scenarioSkillsFingerprint,
} from './scenarioSkillMatch';

/** 一次场景操作绑定账号和平台；切换身份或取消后，排队写入与迟到回调均失效。 */
function operationScope(signal?: AbortSignal) {
  const identity = authService.getSessionIdentity();
  const baseUrl = authService.getBaseUrl();
  let invalidated = false;
  const invalidate = () => {
    invalidated = true;
  };
  window.addEventListener('auth:identityChanging', invalidate);
  return {
    /** 每次请求前后检查；已发给旧账号的请求可能完成，但不会用于新账号。 */
    assertCurrent() {
      if (
        !identity ||
        invalidated ||
        signal?.aborted ||
        authService.getSessionIdentity() !== identity ||
        authService.getBaseUrl() !== baseUrl
      ) {
        throw new DOMException('Scenario operation cancelled', 'AbortError');
      }
    },
    /** 无论成功、失败或取消，都释放身份订阅。 */
    dispose() {
      window.removeEventListener('auth:identityChanging', invalidate);
    },
  };
}

/** 读取权威列表，不使用跨用户单例缓存，也不把网络失败解释为“没有安装”。 */
async function readSkills(scope: ReturnType<typeof operationScope>): Promise<Skill[]> {
  scope.assertCurrent();
  const result = await window.electron.skills.list();
  scope.assertCurrent();
  if (!result.success || !Array.isArray(result.skills))
    throw new Error(result.error || 'Failed to read scenario skills');
  return result.skills;
}

/** 只探测技能状态；不安装、不启用，不保留会跨账号/登录周期复用的解析缓存。 */
export async function resolveScenarioSkills(
  scenario: MyScenario | null | undefined,
  _options?: { force?: boolean }
): Promise<ScenarioSkillResolution> {
  const scope = operationScope();
  try {
    scope.assertCurrent();
    if (scenario) await scenarioService.assertScenarioAccess(scenario);
    scope.assertCurrent();
    return matchScenarioSkills(scenario?.skills ?? [], await readSkills(scope));
  } finally {
    scope.dispose();
  }
}

/** 准备结果包含权威列表和逐项失败，界面只能在同一身份/请求仍有效时同步。 */
export interface EnsureScenarioSkillsResult extends ScenarioSkillResolution {
  installed: number;
  enabled: number;
  failed: ScenarioSkillEntry[];
  localSkills: Skill[];
}

/** 用户确认后安装缺失项、启用停用项；支持取消未发出的操作，完成后重新校验真实启用状态。 */
export async function ensureScenarioSkills(
  scenario: MyScenario,
  options?: {
    /** UI 卸载/取消后停止排队请求，不承诺撤销服务端已经完成的安装。 */
    signal?: AbortSignal;
    onProgress?: (slug: string, state: ScenarioSkillState, error?: string) => void;
    concurrency?: number;
  }
): Promise<EnsureScenarioSkillsResult> {
  const scope = operationScope(options?.signal);
  let installed = 0;
  let enabled = 0;
  const errors = new Map<string, string>();
  /** 进度也受身份检查保护，不向新页面回写旧账号状态。 */
  const progress = (slug: string, state: ScenarioSkillState, error?: string) => {
    scope.assertCurrent();
    options?.onProgress?.(slug, state, error);
  };
  try {
    scope.assertCurrent();
    // 准备弹窗可能在撤权后仍然打开，安装或启用前重新验证平台权限。
    await scenarioService.assertScenarioAccess(scenario);
    scope.assertCurrent();
    const initial = await readSkills(scope);
    const resolution = matchScenarioSkills(scenario.skills, initial);
    const actionable = resolution.entries.filter(
      (entry) => entry.state === 'missing' || entry.state === 'disabled'
    );
    await runWithConcurrency(
      actionable,
      Math.max(1, Math.min(3, Math.floor(options?.concurrency ?? 3) || 1)),
      async (entry) => {
        scope.assertCurrent();
        const slug = entry.link.skill_name;
        progress(slug, 'installing');
        try {
          const result =
            entry.state === 'disabled'
              ? await window.electron.skills.setEnabled({ id: entry.skill!.id, enabled: true })
              : await window.electron.skills.installOnline({ slug, featured: true });
          scope.assertCurrent();
          if (!result.success) throw new Error(result.error || 'Failed to prepare scenario skill');
          if (entry.state === 'disabled') enabled++;
          else installed++;
        } catch (error) {
          // 取消不降级为普通失败，必须停止后续队列及整个调用的页面副作用。
          scope.assertCurrent();
          const message =
            error instanceof Error ? error.message : 'Failed to prepare scenario skill';
          errors.set(normalizeSkillKey(slug), message);
          progress(slug, 'failed', message);
        }
      }
    );
    const localSkills = actionable.length ? await readSkills(scope) : initial;
    const refreshed = matchScenarioSkills(scenario.skills, localSkills);
    const entries = refreshed.entries.map((entry) => {
      if (entry.state === 'ready' || entry.state === 'unavailable') return entry;
      return {
        ...entry,
        state: 'failed' as const,
        error: errors.get(normalizeSkillKey(entry.link.skill_name)),
      };
    });
    for (const entry of entries) progress(entry.link.skill_name, entry.state, entry.error);
    return {
      ...refreshed,
      entries,
      localSkills,
      installed,
      enabled,
      failed: entries.filter((entry) => entry.state === 'failed'),
    };
  } finally {
    scope.dispose();
  }
}
